import { findIndex } from "@dhx/ts-common/core";
import { getFormattedDate } from "@dhx/ts-common/date";
import { CellFormats, IFormats, ISpreadsheet, ISpreadsheetConfig } from "../types";
import locale from "./../locales/en";
import { excelDateToJSDate, timeStringToJS } from "./date";

let config: ISpreadsheetConfig = {};
let defaultFormats: IFormats[] = [];
let separators = { decimal: ".", thousands: "," };
let currency = "$";

const currencyRegex = "(\\[\\$[\\w\\W]*?\\]|[\\$]{0,1})";
const pureCurrency = "\\[\\$([\\w\\W]*?)\\]|(\\$)"; // custom currency - [$EUR]#,##0.00
const sep = "([,. ]{0,1})";
const percent = "([%]{0,1})";
const allDigits = "([#0]*)";

const pureCurrencyRegex = new RegExp(pureCurrency);

const formatRegex = new RegExp(
	`${currencyRegex}${allDigits}${sep}${allDigits}${sep}${allDigits}${percent}${currencyRegex}`
);

const formats = {};

function getCurrencySymbol(): string {
	return "$";
}

function getSeparators() {
	return separators;
}

function _partsToObj(parts: any[]): any {
	return {
		currencyBefore: parts[1],

		digits: parts[2],
		thousandsSeparator: parts[3],
		optionalDigits: parts[4],

		decimalSeparator: parts[5],
		decimalDigits: parts[6],

		percent: parts[7],

		currencyAfter: parts[8],

		decimalLength: 0,
	};
}

export function getDefaultFormats() {
	return defaultFormats;
}

export function getFormat(id: string): IFormats {
	return defaultFormats[findIndex(defaultFormats, f => f.id === id)];
}

export function isNumeric(val: any): boolean {
	return !isNaN(val - parseFloat(val));
}

function getFormatRule(format: string): any {
	if (!format || typeof format !== "string") {
		return;
	}
	if (formats[format]) {
		return formats[format];
	}

	// eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
	const parts = format.match(formatRegex);

	const rule = _partsToObj(parts);

	if (!rule.decimalSeparator && rule.thousandsSeparator) {
		rule.decimalSeparator = rule.thousandsSeparator;
		rule.decimalDigits = rule.optionalDigits;

		rule.thousandsSeparator = "";
		rule.optionalDigits = "";
	}

	if (rule.currencyBefore) {
		const m = rule.currencyBefore.match(pureCurrencyRegex);
		rule.currencyBefore = m[1] || m[2];
	}
	if (rule.currencyAfter) {
		const m = rule.currencyAfter.match(pureCurrencyRegex);
		rule.currencyAfter = m[1] || m[2];
	}

	if (rule.percent) {
		rule.currencyBefore = "";
		rule.currencyAfter = "";
	}

	rule.decimalLength = rule.decimalDigits ? rule.decimalDigits.split("0").length - 1 : rule.decimalLength;
	if (format === "common") {
		rule.decimalSeparator = ".";
		rule.decimalLength = "";
		rule.thousandsSeparator = ",";
	}
	formats[format] = rule;

	return rule;
}

function setDefaultFormats() {
	defaultFormats.forEach(format => {
		getFormatRule(format.mask);
	});
}

function numberWithSeparators(val: string, thousand = "", decimal: string, optionalDigits = 3): string {
	const parts = val.toString().split(".");
	let digits = [];
	let index = 1;
	for (let i = parts[0].length - 1; i > -1; i--) {
		digits.push(parts[0][i]);
		if (index === optionalDigits && i) {
			digits.push(thousand);
			index = 1;
			continue;
		}
		index++;
	}
	parts[0] = digits.reverse().join("");
	return parts.join(decimal);
}

function formatByRule(val: string, rule: any): string {
	if (!rule) {
		return val;
	}
	if (!isNumeric(val)) {
		return val;
	}

	let formattedVal = "";
	if (rule.currencyBefore) {
		formattedVal += rule.currencyBefore;
	}

	let num = parseFloat(val);
	const isNegative = num < 0;

	if (isNegative) {
		num = Math.abs(num);
	}
	let numAsString = num.toString();
	if (rule.percent) {
		num *= 100;
	}

	if (rule.digits) {
		numAsString = num.toFixed(rule.decimalLength || 2);
	}
	const digits = rule?.digits + rule?.optionalDigits;
	const parts = numAsString.split(".");
	if (digits && digits.length > parts[0].length) {
		let val = parts[0].split("");
		let newStr = parts[0];
		let index = 0;
		for (let i = digits.length - 1; i > -1; i--) {
			const ruleChar = digits[i];
			const char = val[index];
			if (ruleChar === "0" && !char) {
				newStr = "0" + newStr;
			}
			index++;
		}
		numAsString = newStr + (parts.length > 1 ? `.${parts[1]}` : "");
	}

	formattedVal += rule.digits
		? numberWithSeparators(
				numAsString,
				rule.thousandsSeparator,
				rule.decimalSeparator,
				rule.optionalDigits.length
		  )
		: numAsString;

	formattedVal += rule.percent;

	if (rule.currencyAfter) {
		formattedVal += rule.currencyAfter;
	}
	if (isNegative) {
		formattedVal = `-${formattedVal}`;
	}
	return formattedVal;
}

export function initFormat(spreadsheet: ISpreadsheet, dateFormat?: string, timeFormat?: number) {
	config = spreadsheet.config;
	defaultFormats = [
		{ name: locale.common, id: "common", mask: "", example: "1500.31" },
		{ name: locale.number, id: "number", mask: "#,##0.00", example: "1500.31" },
		{
			name: locale.percent,
			id: "percent",
			mask: "#,##0.00%",
			example: "15.0031",
		},
		{
			name: locale.currency,
			id: "currency",
			mask: "$#,##0.00",
			example: "1500.31",
		},
		{
			name: locale.date,
			id: "date",
			mask: "mm-dd-yy",
			example: "44490",
			dateFormat,
		},
		{
			name: locale.time,
			id: "time",
			mask: `h:mm:ss ${timeFormat > 12 ? "" : "am/pm"}`,
			example: "0.5625",
			timeFormat,
		},
		{
			name: locale.text,
			id: "text",
			mask: "@",
			example: "some text",
		},
	];
	config.formats.forEach(f => {
		const ind = findIndex(defaultFormats, df => df.id === f.id);
		if (ind >= 0) {
			defaultFormats[ind] = f;
		} else {
			defaultFormats.push(f);
		}
	});
	const numberFormatRule = getFormatRule(getFormat("number").mask);
	separators = {
		decimal: numberFormatRule.decimalSeparator,
		thousands: numberFormatRule.thousandsSeparator,
	};
	const currencyFormatRule = getFormatRule(getFormat("currency").mask);
	currency = currencyFormatRule.currency || currency;

	config.formats = defaultFormats;
	setDefaultFormats();
}

export function getDefaultFormatsMap() {
	return defaultFormats.reduce((acc, f) => {
		acc[f.mask] = f.id;
		return acc;
	}, {});
}

export function getPureNumber(val: string): number {
	const isPercent = typeof val === "string" && val.includes("%");
	const num = parseFloat(`${val}`.replace(/[^.\d]/g, ""));
	return isPercent ? num / 100 : num;
}

export function getCleanValue(val: string): string {
	const separatorsObj = getSeparators();
	const cur = getCurrencySymbol();
	const customFormats = getDefaultFormats().filter(i => i.mask.startsWith("[$")) || [];
	const symbols = customFormats.map(i => i.mask.substring(2, i.mask.indexOf("]")));
	if (symbols.length) {
		for (let i = 0; i < symbols.length; i++) {
			const symbol = symbols[i];
			if (val.toString().startsWith(symbol)) {
				return getCleanValue(`${val}`.substr(symbol.length));
			}
		}
	}

	return `${val}`
		.replace(new RegExp(`[${separatorsObj.thousands}${cur}%s]`, "g"), "")
		.replace(new RegExp(`[${separatorsObj.decimal}]`, "g"), ".");
}

export function getUnformattedValue(val: any, format: CellFormats) {
	if (format === "common") {
		return val;
	}
	const clean = getCleanValue(val);
	if (format === "percent") {
		return (parseFloat(clean) / 100).toFixed(4);
	}
	return clean;
}

export function detectCellFormat(val: any): CellFormats {
	const currencySymbol = getCurrencySymbol();

	const isNumber = isNumeric(getCleanValue(val));
	if (!isNumber) {
		return "";
	}
	const stringVal = `${val}`;

	const percentInd = stringVal.indexOf("%");
	const currInd = stringVal.indexOf(currencySymbol);

	const isPercent = percentInd === 0 || percentInd === stringVal.length - 1;
	const isCurrency = currInd === 0;

	if (isPercent) {
		return getFormat("percent").mask;
	}
	if (isCurrency) {
		return getFormat("currency").mask;
	}

	const customFormats = getDefaultFormats().filter(i => i.mask.startsWith("[$")) || [];
	const symbols = customFormats.map(i => i.mask.substring(2, i.mask.indexOf("]")));
	if (symbols.length) {
		for (let i = 0; i < symbols.length; i++) {
			const symbol = symbols[i];
			if (val.toString().startsWith(symbol)) {
				return customFormats[i].mask;
			}
		}
	}

	return getFormat("number").mask;
}

export function getFormattedValue(val: any, format: IFormats): string {
	if (typeof val === "boolean") {
		return `${val}`.toUpperCase();
	}
	if (!format || format.mask === "@") {
		return val;
	}
	if (format.id === "date") {
		if (!isNumeric(val)) {
			return val;
		}
		return getFormattedDate(format.dateFormat, excelDateToJSDate(+val));
	}
	if (format.id === "time") {
		if (!val) return val;
		const time_format = `%${format.timeFormat > 12 ? "H" : "h"}:%i:%s ${format.timeFormat > 12 ? "" : "%A"}`;
		if (!isNumeric(val)) {
			const time = timeStringToJS(val as string) as Date;
			return typeof time === "string" ? time : getFormattedDate(time_format, time);
		}

		return getFormattedDate(time_format, excelDateToJSDate(+val));
	}
	const rule = getFormatRule(format.mask);
	return formatByRule(val, rule);
}
